import React, { useState } from 'react'
import { Download, Copy } from 'lucide-react'
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx'

interface OtherEmbodimentsDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  showHeader?: boolean;
}

/** -------- helpers you were missing -------- **/

// Safely parse JSON-like strings (tolerates ```json fences and smart quotes)
const tryParse = (val) => {
  if (typeof val !== 'string') return val
  let s = val.trim()
  // strip code fences like ```json ... ```
  s = s.replace(/^```(?:json|js|javascript)?\s*/i, '').replace(/```$/i, '')
  // normalize smart quotes
  s = s.replace(/[“”]/g, '"').replace(/[‘’]/g, "'")
  // if extra prose, grab the largest {...} block
  const first = s.indexOf('{')
  const last = s.lastIndexOf('}')
  if (first !== -1 && last !== -1 && last > first) s = s.slice(first, last + 1)
  try { return JSON.parse(s) } catch { return val }
}

// Depth-first search for a key anywhere in an object tree
const deepFindKey = (obj, key) => {
  if (!obj || typeof obj !== 'object') return undefined
  if (Object.prototype.hasOwnProperty.call(obj, key)) return obj[key]
  for (const k of Object.keys(obj)) {
    const v = obj[k]
    const found = deepFindKey(v, key)
    if (found !== undefined) return found
  }
  return undefined
}

// Check if an object looks like the other_embodiments shape
const looksLikeOtherEmbods = (val) => (
  val && typeof val === 'object' && !Array.isArray(val) &&
  ['structural_variations','functional_variations','implementation_alternatives','material_substitutions','scale_variations']
    .some(k => Array.isArray(val[k]))
)

/** ----------------------------------------- **/

const OtherEmbodimentsDisplay: React.FC<OtherEmbodimentsDisplayProps> = ({
  result,
  theme = 'light',
  showToast,
  showHeader = true
}) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({})
  const isDarkMode = theme === 'dark'

  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast?.('Copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy to clipboard');
    }
  };

  // 1) Normalize the incoming result (string or object)
  let parsedResult = typeof result === 'string' ? tryParse(result) : result

  // 2) If result.output is a string, try to parse that too
  if (parsedResult && typeof parsedResult === 'object' && typeof parsedResult.output === 'string') {
    const parsedOutput = tryParse(parsedResult.output)
    if (parsedOutput && typeof parsedOutput === 'object') {
      parsedResult = { ...parsedResult, output: parsedOutput }
    }
  }

  // 3) Locate other_embodiments (shallow or deep)
  let otherEmbodiments =
    parsedResult?.other_embodiments ??
    parsedResult?.output?.other_embodiments ??
    deepFindKey(parsedResult, 'other_embodiments')

  // 4) As a last resort, if the whole object already matches the shape, use it
  if (!looksLikeOtherEmbods(otherEmbodiments) && looksLikeOtherEmbods(parsedResult)) {
    otherEmbodiments = parsedResult
  }

  if (!looksLikeOtherEmbods(otherEmbodiments)) {
    // If original was a plain string and parse failed, show the raw string
    if (typeof result === 'string' && typeof parsedResult === 'string') {
      return (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
          <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'} whitespace-pre-wrap`}>
            {result}
          </div>
        </div>
      )
    }
    // Structured not found
    return (
      <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-700'}`}>
        No structured other embodiments data available
      </div>
    )
  }

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  const sectionConfigs = {
    structural_variations: { title: 'Structural Variations', emoji: '🔧' },
    functional_variations: { title: 'Functional Variations', emoji: '⚙️' },
    implementation_alternatives: { title: 'Implementation Alternatives', emoji: '💻' },
    material_substitutions: { title: 'Material Substitutions', emoji: '🔄' },
    scale_variations: { title: 'Scale Variations', emoji: '📏' }
  }

  const exportToDocx = async () => {
    try {
      const children: Paragraph[] = [];

      // Title
      children.push(
        new Paragraph({
          text: "Other Embodiments",
          heading: HeadingLevel.HEADING_1,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 },
        })
      );

      // Generate sections
      Object.entries(sectionConfigs).forEach(([sectionKey, config]) => {
        const items = otherEmbodiments[sectionKey];
        if (!Array.isArray(items) || items.length === 0) return;

        // Section heading
        children.push(
          new Paragraph({
            text: config.title,
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 300, after: 200 },
          })
        );

        items.forEach((item: any, index: number) => {
          const displayName = item?.name ??
            (sectionKey === 'material_substitutions' ? item?.original_component : undefined) ??
            (sectionKey === 'scale_variations' ? item?.scale_type : undefined) ??
            `Item ${index + 1}`;

          const description = item?.description ?? '';

          // Item name
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: displayName,
                  bold: true,
                }),
              ],
              spacing: { before: 200, after: 100 },
              indent: { left: 360 },
            })
          );

          // Description
          if (description) {
            children.push(
              new Paragraph({
                text: description,
                spacing: { after: 100, line: 360 },
                indent: { left: 360 },
              })
            );
          }

          // Special handling for material substitutions
          if (sectionKey === 'material_substitutions') {
            if (Array.isArray(item.alternative_materials) && item.alternative_materials.length > 0) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Alternative Materials: ', bold: true }),
                    new TextRun({ text: item.alternative_materials.join(', ') }),
                  ],
                  spacing: { after: 50 },
                  indent: { left: 360 },
                })
              );
            }
            if (item.original_component) {
              children.push(
                new Paragraph({
                  children: [
                    new TextRun({ text: 'Original Component: ', bold: true }),
                    new TextRun({ text: item.original_component }),
                  ],
                  spacing: { after: 100 },
                  indent: { left: 360 },
                })
              );
            }
          }

          // Special handling for scale variations
          if (sectionKey === 'scale_variations' && item.scale_type) {
            children.push(
              new Paragraph({
                children: [
                  new TextRun({ text: 'Scale Type: ', bold: true }),
                  new TextRun({ text: item.scale_type }),
                ],
                spacing: { after: 100 },
                indent: { left: 360 },
              })
            );
          }
        });
      });

      // Create Word document
      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      // Generate and download the document
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'other-embodiments.docx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      if (showToast) {
        showToast('Other Embodiments exported to Word document');
      }
    } catch (error) {
      console.error('Error exporting Other Embodiments to DOCX:', error);
      if (showToast) {
        showToast('Error exporting Other Embodiments');
      }
    }
  }

  const renderSection = (sectionKey, items) => {
    if (!Array.isArray(items) || items.length === 0) return null

    const config = sectionConfigs[sectionKey] || { title: sectionKey, emoji: '📝' }
    const isExpanded = expandedSections[sectionKey] !== false // Default to expanded

    return (
      <div key={sectionKey} className={`mb-6 border rounded-lg ${isDarkMode ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        {/* Section Header */}
        <div 
          className={`flex items-center justify-between p-4 cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
          onClick={() => toggleSection(sectionKey)}
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">{config.emoji}</span>
            <div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {config.title}
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {items.length} item{items.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {isExpanded ? '▲' : '▼'}
          </span>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            {items.map((item, index) => {
              const displayName =
                item?.name ??
                (sectionKey === 'material_substitutions' ? item?.original_component : undefined) ??
                (sectionKey === 'scale_variations' ? item?.scale_type : undefined) ??
                `Item ${index + 1}`

              const description = item?.description ?? ''

              return (
                <div key={index} className="mb-4 last:mb-0">
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="flex items-start justify-between mb-2">
                      <span className={`text-xs font-medium px-2 py-1 rounded ${isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                        {displayName}
                      </span>
                      <button
                        onClick={() => copyToClipboard(`${displayName}\n\n${description}`)}
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                      >
                        <Copy className="w-3 h-3" />
                        Copy Item
                      </button>
                    </div>
                    <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                      {description}
                    </div>

                    {/* Special handling for material substitutions */}
                    {sectionKey === 'material_substitutions' && Array.isArray(item.alternative_materials) && item.alternative_materials.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                        <div className="flex flex-wrap gap-2 mb-2">
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Alternative Materials:
                          </span>
                          {item.alternative_materials.map((material, matIndex) => (
                            <span key={matIndex} className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-200 text-gray-800'}`}>
                              {material}
                            </span>
                          ))}
                        </div>
                        {item.original_component && (
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            Original: <span className="font-medium">{item.original_component}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Special handling for scale variations */}
                    {sectionKey === 'scale_variations' && item.scale_type && (
                      <div className="mt-3 pt-3 border-t border-gray-300 dark:border-gray-600">
                        <span className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-indigo-900 text-indigo-300' : 'bg-indigo-100 text-indigo-800'}`}>
                          Scale Type: {item.scale_type}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}

            {/* Section Actions */}
            <div className="flex gap-2 mt-4 pt-3 border-t border-gray-300 dark:border-gray-600">
              <button
                onClick={() => {
                  const sectionText = items.map((item, i) => {
                    const nm = item?.name ?? item?.original_component ?? item?.scale_type ?? `Item ${i + 1}`
                    const desc = item?.description ?? ''
                    return `${nm}\n\n${desc}`
                  }).join('\n\n---\n\n');
                  copyToClipboard(sectionText);
                }}
                className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-3 h-3" />
                Copy Section
              </button>
            </div>
          </div>
        )}
      </div>
    )
  }

  // Expand/Collapse All Controls
  const allSections = Object.keys(otherEmbodiments)
    .filter(key => Array.isArray(otherEmbodiments[key]) && otherEmbodiments[key].length > 0)
  
  const allSectionsText = Object.entries(otherEmbodiments)
    .filter(([_, items]) => Array.isArray(items) && items.length > 0)
    .map(([sectionKey, items]: [string, any]) => {
      const config = sectionConfigs[sectionKey] || { title: sectionKey }
      return `# ${config.title}\n\n${(items as any[]).map((item, i) => {
        const nm = item?.name ?? item?.original_component ?? item?.scale_type ?? `Item ${i + 1}`
        const desc = item?.description ?? ''
        return `## ${nm}\n\n${desc}`
      }).join('\n\n')}`
    })
    .join('\n\n---\n\n')

  return (
    <div className="space-y-4">
      {showHeader && (
        <>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 rounded-lg ${isDarkMode ? 'bg-blue-900 bg-opacity-30' : 'bg-blue-50'}`}>
            <div className="flex items-center gap-3">
              <span className="text-2xl">🔄</span>
              <div>
                <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Other Embodiments
                </h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {allSections.length} section{allSections.length !== 1 ? 's' : ''} generated
                </p>
              </div>
            </div>

            {/* Global Actions */}
            <div className="flex gap-2">
              <button
                onClick={exportToDocx}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-green-700 hover:bg-green-600 text-green-100' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                title="Export other embodiments as Word document"
              >
                <Download className="w-4 h-4" />
                Export DOCX
              </button>
              <button
                onClick={() => copyToClipboard(allSectionsText)}
                className={`text-sm px-3 py-1 rounded flex items-center gap-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
              >
                <Copy className="w-4 h-4" />
                Copy All
              </button>
            </div>
          </div>

          {/* Expand/Collapse All Controls */}
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => {
                const allExpanded = {}
                allSections.forEach(sectionKey => { allExpanded[sectionKey] = true })
                setExpandedSections(allExpanded)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Expand All
            </button>
            <button
              onClick={() => {
                const allCollapsed = {}
                allSections.forEach(sectionKey => { allCollapsed[sectionKey] = false })
                setExpandedSections(allCollapsed)
              }}
              className={`text-xs px-2 py-1 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
            >
              Collapse All
            </button>
          </div>
        </>
      )}

      {/* Sections */}
      <div className="space-y-4">
        {Object.entries(otherEmbodiments).map(([sectionKey, items]) => renderSection(sectionKey, items))}
      </div>
    </div>
  )
}

export default OtherEmbodimentsDisplay
